/**
 * A slim strip above the left column saying why it shows the turn it shows.
 *
 * While a turn is being worked out the panel deliberately stays on the last turn
 * it finished, and once the reader opens an older turn it stops moving at all.
 * Both are easy to misread as the panel being stuck, so the strip says which of
 * the two is happening and offers the way back to live.
 */
import { Loader2, Radio } from "lucide-react";

interface LiveTurnStatusProps {
  /**
   * True while a simulation is running or a reply is on its way. Once the chat
   * stops moving there is nothing to keep up with, so the strip goes away.
   */
  isLive: boolean;
  isFollowingLive: boolean;
  isEvaluatingTurn: boolean;
  /**
   * The turn the panel is showing.
   */
  turnNumber?: number;
  onFollowLive?: () => void;
}

export const LiveTurnStatus = ({
  isLive,
  isFollowingLive,
  isEvaluatingTurn,
  turnNumber,
  onFollowLive,
}: LiveTurnStatusProps) => {
  if (!isLive) return null;

  if (!isFollowingLive) {
    return (
      <div className="flex flex-none items-center gap-2.5 rounded-lg border border-brand-border bg-brand-background px-3 py-2">
        <Radio
          className="h-3.5 w-3.5 flex-none text-brand-muted"
          aria-hidden={true}
        />
        <span className="min-w-0 flex-1 text-xs text-brand-muted">
          {turnNumber !== undefined
            ? `Holding on turn ${turnNumber}. New turns will not move this panel.`
            : "Holding. New turns will not move this panel."}
        </span>
        {onFollowLive && (
          <button
            type="button"
            onClick={onFollowLive}
            className="flex-none rounded-md border border-unlikely/30 bg-unlikely/[0.08] px-2.5 py-1 text-[11px] font-semibold text-unlikely transition-colors hover:bg-unlikely/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40"
          >
            Follow the latest turn
          </button>
        )}
      </div>
    );
  }

  // Between turns the chat is live but nothing is being worked out, so there is
  // nothing to explain.
  if (!isEvaluatingTurn) return null;

  return (
    <div
      role="status"
      className="flex flex-none items-center gap-2.5 rounded-lg border border-unlikely/25 bg-unlikely/[0.05] px-3 py-2"
    >
      <Loader2
        className="h-3.5 w-3.5 flex-none animate-spin text-unlikely motion-reduce:animate-none"
        aria-hidden={true}
      />
      <span className="text-xs text-brand-muted">
        Working out the customer&rsquo;s latest reply.
        {turnNumber !== undefined
          ? ` Showing turn ${turnNumber} until it lands.`
          : ""}
      </span>
    </div>
  );
};
